import * as React from 'react'
import { createContext, useContext, useState, useCallback } from 'react'
import { DefaultTheme, ThemeProvider } from 'styled-components'

import { ConfirmDialog, TConfirmDialogOptions } from '../components/confirm/Confirm'

const defaultPromise = new Promise<void>((resolve) => resolve())

export const ConfirmContext = createContext<(options: TConfirmDialogOptions) => Promise<void>>(
  () => defaultPromise
)

export const useConfirm = () => {
  return useContext(ConfirmContext)
}

const DEFAULT_OPTIONS: TConfirmDialogOptions = {
  title: 'Confirm',
  content: 'You cannot undo this action.',
  dialogType: 'default',
  confirmationText: 'Confirm',
  cancellationText: 'Cancel',
  cosmicVersion: false,
}

type IConfirmProvider = {
  children?: React.ReactNode
}

export const ConfirmProvider = ({ children }: IConfirmProvider) => {
  const [options, setOptions] = useState<TConfirmDialogOptions>(DEFAULT_OPTIONS)
  const [resolveReject, setResolveReject] = useState<Array<() => void>>([])
  const [resolve, reject] = resolveReject

  const confirm = useCallback((confirmOptions: TConfirmDialogOptions) => {
    return new Promise<void>((resolve, reject) => {
      setOptions(confirmOptions)
      setResolveReject([resolve, reject])
    })
  }, [])

  const handleClose = useCallback(() => {
    setResolveReject([])
  }, [])

  const handleCancel = useCallback(() => {
    reject()
    handleClose()
  }, [reject, handleClose])

  const handleConfirm = useCallback(() => {
    resolve()
    handleClose()
  }, [resolve, handleClose])

  return (
    <>
      <ConfirmContext.Provider value={confirm}>{children}</ConfirmContext.Provider>
      <ThemeProvider
        theme={(t: DefaultTheme): DefaultTheme => ({
          ...t,
          // Elevate backdrop z-index so the confirm overlay renders above Drawer panels
          zIndex: {
            ...t.zIndex,
            backdrop: t.zIndex.modal as unknown as typeof t.zIndex.backdrop,
          },
        })}
      >
        <ConfirmDialog
          isOpen={resolveReject.length === 2}
          options={{ ...DEFAULT_OPTIONS, ...options }}
          onClose={handleClose}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      </ThemeProvider>
    </>
  )
}
